import type { CogniPrintProfile, DiagnosticDimensionKey } from '@shared/types/cogniprint';
import { DIMENSION_DISPLAY, getLearningApproachLabel, getDepthCategory, getInfoDensityCategory, getViewpointCategory } from '@shared/types/cogniprint';
import { isLearningAxisBalanced } from '@shared/constants/confidence-bands';
import { isAssessed } from '@shared/utils/profile-validation';
import { evidenceStatusFor } from '@shared/constants/evidence-status';

export interface DimensionDisplayData {
  key: string;
  name: string;
  value: number;       // 0-100 representative score
  label: string;       // human-readable label (e.g. "Visual", "Top-down")
  color: string;       // hex color
  description: string; // user-facing description
  tier: 'free' | 'paid';
  locked: boolean;     // true if paid dimension on free tier
  summary: string;     // personalised explanation of what this score means
  confidence: number;  // 0.0-1.0 — per-dimension confidence from Adaptive Engine
  /** False when this dimension was never assessed or was stood in for with a
   *  centre value. Such a dimension must never be rendered as a finding. */
  assessed: boolean;
  /** 'preference' (self-report) or 'measured' (behavioural). Drives the UI's verb
   *  so a claim cannot outrun its evidence. See @shared/constants/evidence-status. */
  evidence: 'preference' | 'measured';

  // ─── How to draw it honestly ─────────────────────────────────────────────
  // Three dimension shapes need three different pictures. Drawing them all as
  // one bar is how a three-way split (visual 34 · text 33 · spoken 33) came to
  // read as "Visual — low". A spread is not a low score.
  kind: 'scalar' | 'weighted' | 'axes' | 'perUpload';
  /** Weighted dimensions: every option's share, largest first. Sums to 100. */
  segments?: Array<{ key: string; label: string; share: number; colour: string }>;
  /** Learning Approach: two independent axes, each 0–100 between two poles. */
  axes?: Array<{ key: string; lowPole: string; highPole: string; value: number; balanced: boolean }>;
  /** Weighted dimensions only: does one option actually dominate? */
  clarity?: 'clear' | 'leaning' | 'mixed';
  /** True only where `value` is a position on a scale and a number means something. */
  showNumber: boolean;
}

/** Where a weighted result stops being "one preference" and becomes "a spread". */
export const CLARITY_CLEAR_MIN = 60;
export const CLARITY_LEANING_MIN = 45;

export function clarityOf(shares: number[]): 'clear' | 'leaning' | 'mixed' {
  const top = Math.max(...shares);
  if (top >= CLARITY_CLEAR_MIN) return 'clear';
  if (top >= CLARITY_LEANING_MIN) return 'leaning';
  return 'mixed';
}

/** Shade a dimension's colour for its ranked segments — strongest share, strongest colour. */
const SEGMENT_ALPHAS = ['ff', 'b3', '80', '4d', '26'];

function toSegments(
  score: Record<string, number>,
  labels: Record<string, string>,
  colour: string,
): NonNullable<DimensionDisplayData['segments']> {
  return Object.entries(score)
    .map(([key, share]) => ({ key, label: labels[key] ?? key, share }))
    .sort((a, b) => b.share - a.share)
    .map((seg, i) => ({ ...seg, colour: `${colour}${SEGMENT_ALPHAS[Math.min(i, SEGMENT_ALPHAS.length - 1)]}` }));
}

/** The headline for a weighted dimension. Never claims a winner that isn't one. */
function weightedLabel(
  segments: NonNullable<DimensionDisplayData['segments']>,
  clarity: 'clear' | 'leaning' | 'mixed',
): string {
  const top = segments[0];
  if (clarity === 'clear') return top.label;
  if (clarity === 'leaning') return `Leans ${top.label.toLowerCase()}`;
  return 'Mixed';
}

/** The names people actually use for the pieces, for legends and summaries. */
const FORMAT_LABELS: Record<string, string> = {
  visual: 'Visual', textual: 'Text', auditory: 'Spoken', interactive: 'Hands-on', video: 'Video',
};
const SEQUENCING_LABELS: Record<string, string> = {
  topDown: 'Big picture first', bottomUp: 'Basics first', modular: 'Jump to what I need',
};
const MOTIVATION_LABELS: Record<string, string> = {
  mastery: 'Getting good at it', performance: 'Results', social: 'Other people', curiosity: 'Curiosity',
};

/** The copy shown where a dimension has no evidence behind it. Never invents a score. */
const NOT_ASSESSED_SUMMARY =
  "This one wasn't captured in your diagnostic, so we haven't got a reading on it yet. Retake the Full Diagnostic to assess it.";

/**
 * Returns a confidence-tier label for UI theming. Research §10 recommends
 * a three-band treatment: Low (<0.5), Building (0.5–0.75), Strong (≥0.75).
 */
export type ConfidenceTier = 'low' | 'building' | 'strong';

export function confidenceTier(c: number): ConfidenceTier {
  if (c < 0.5) return 'low';
  if (c < 0.75) return 'building';
  return 'strong';
}

/** Generate a personalised summary for a dimension based on its score/label */
function buildDimensionSummary(key: string, label: string): string {
  switch (key) {
    case 'contentFormat': {
      // `label` is 'Mixed', 'Leans x' or 'X'; `value` is the top share.
      if (label === 'Mixed') return `Your answers were spread across several formats rather than landing on one — that's a real result, not a weak one. It usually means the right format depends on what you're learning. CogniPrint will vary the format and watch which ones you actually stay with.`;
      if (label.startsWith('Leans')) return `You lean towards ${label.replace('Leans ', '').toLowerCase()} material, but not exclusively — you picked something else at least once. CogniPrint will lead with ${label.replace('Leans ', '').toLowerCase()} and keep the other formats close.`;
      return `Every answer you gave pointed the same way: ${label.toLowerCase()}. That's about as clear as a preference gets from a few questions. CogniPrint will lead heavily with ${label.toLowerCase()} material, and will tell you if what you actually engage with starts to disagree.`;
    }

    case 'learningApproach':
      // 'Balanced' comes from the confidence bands: one or both Kolb axes landed
      // in the 41–59 zone, where the quadrant label is an artefact of rounding.
      if (label === 'Balanced') return `Your answers put you close to the middle on how you like to start — theory or practice, watching or doing. That's a real result, not a missing one: CogniPrint will mix both rather than commit you to one, and will sharpen this as it sees what you actually engage with.`;
      if (label === 'Accommodating') return `You're an Accommodating learner — you prefer hands-on experience and learn best by doing rather than theorising. CogniPrint will lead with practical examples and real-world applications before introducing underlying theory.`;
      if (label === 'Converging') return `You're a Converging learner — you like applying theory to practical problems. CogniPrint will pair concepts with concrete exercises so you can immediately put ideas to work.`;
      if (label === 'Diverging') return `You're a Diverging learner — you thrive on observation and brainstorming from multiple perspectives. CogniPrint will present varied viewpoints and reflective prompts to support your style.`;
      return `You're an Assimilating learner — you prefer logical, structured explanations and abstract concepts. CogniPrint will lead with clear frameworks and theory before moving to examples.`;

    case 'depthTolerance':
      // Band labels can be compound ("Standard–Deep") when a score sits in the
      // transition zone. Say so, rather than forcing a side.
      if (label.includes('–')) return `You sit between ${label.split('–')[0].toLowerCase()} and ${label.split('–')[1].toLowerCase()} on how much detail you want — enough of the "why" to trust it, without every last caveat. CogniPrint will give you solid explanations and keep the deeper layer one tap away, and will watch which way you actually lean.`;
      if (label === 'Essentials') return `You prefer the essentials — concise explanations that get straight to the point. CogniPrint will keep content focused and avoid unnecessary detail, giving you just what you need to understand and apply concepts.`;
      if (label === 'Standard') return `You like a balanced level of depth — enough detail to understand the "why" without getting bogged down. CogniPrint will provide solid explanations with optional deeper dives when relevant.`;
      if (label === 'Deep') return `You enjoy going deep into topics and understanding the nuances. CogniPrint will provide thorough explanations with supporting evidence and context to satisfy your curiosity.`;
      return `You want the whole picture — every layer, every exception. CogniPrint will give you comprehensive breakdowns with full context, caveats and the advanced detail.`;

    case 'sequencing': {
      if (label === 'Mixed') return `You didn't settle on one way of moving through material — sometimes you want the map first, sometimes the basics, sometimes just the bit you need. CogniPrint will offer an overview up front and let you choose your route from there.`;
      if (label.startsWith('Leans')) return `You lean towards "${label.replace('Leans ', '').toLowerCase()}", with some flexibility. CogniPrint will default to that and make it easy to switch.`;
      if (label === 'Big picture first') return `Every answer said the same thing: you want to see the whole shape before the parts. CogniPrint will open each topic with an overview and only then drill into detail.`;
      if (label === 'Basics first') return `You build from the ground up — you want the foundations solid before anything is stacked on them. CogniPrint will sequence each topic from first steps to bigger ideas.`;
      return `You go straight to what you need and skip the rest. CogniPrint will break topics into self-contained pieces you can take in any order.`;
    }

    case 'infoDensity':
      if (label.includes('–')) return `You're between ${label.split('–')[0].toLowerCase()} and ${label.split('–')[1].toLowerCase()} on how much you like at once — not one idea at a time, not a wall of text either. CogniPrint will group related points together and break before the next group, and adjust as it sees where you slow down.`;
      if (label === 'Low') return `You take information in best in smaller, focused pieces. CogniPrint will break content into short sections with clear breaks, keeping each piece digestible and avoiding overload.`;
      if (label === 'Medium') return `You handle a moderate amount at once comfortably. CogniPrint will balance thoroughness with readability, grouping related ideas without overwhelming any one section.`;
      return `You're comfortable with dense, information-rich material. CogniPrint will pack more into each section and cut repetition, trusting you to absorb it.`;

    case 'motivation': {
      if (label === 'Mixed') return `No single thing keeps you going — your answers split between getting good at it, seeing results, other people and plain curiosity. That's common and it's useful: it means more than one kind of nudge works on you. CogniPrint will vary how it keeps you moving and notice which one you respond to.`;
      if (label.startsWith('Leans')) return `Mostly it's ${label.replace('Leans ', '').toLowerCase()} that keeps you at it, with something else in the mix. CogniPrint will lead with that.`;
      if (label === 'Getting good at it') return `You keep going because you want to be properly good at the thing — not just finished with it. CogniPrint will mark real progress in skill, not just pages turned.`;
      if (label === 'Results') return `You're moved by something measurable: a score, a deadline, a thing you can point at. CogniPrint will keep those visible and keep them honest.`;
      if (label === 'Other people') return `Learning is social for you — company and conversation keep you going. CogniPrint will surface places to discuss and compare, not just read.`;
      return `Curiosity is what keeps you going — the next question, the surprising connection. CogniPrint will leave threads for you to pull on rather than closing every topic neatly.`;
    }

    case 'viewpointRange':
      if (label.includes('–')) return `You're between ${label.split('–')[0].toLowerCase()} and ${label.split('–')[1].toLowerCase()} on wanting other points of view — you'll take a counter-argument when it earns its place, not for its own sake. CogniPrint will give you the main explanation and include a second view where it genuinely changes the picture.`;
      if (label === 'Low') return `You prefer one clear, authoritative explanation without the debate. CogniPrint will give you the clean answer and keep alternative views available rather than in the way.`;
      if (label === 'Medium') return `You like to see a second point of view, but not every angle. CogniPrint will include relevant counterpoints where they add something real.`;
      return `You want the full picture — the disagreement, the alternatives, the other side. CogniPrint will actively show contrasting views and the debate around each topic.`;

    case 'priorKnowledge':
      return `This dimension is assessed per upload based on how much you already know about that specific topic. It helps CogniPrint calibrate the starting difficulty and skip material you've already mastered.`;

    default:
      return `This dimension helps CogniPrint personalise your learning experience.`;
  }
}

/** Extract a single representative 0-100 value and label for each dimension */
export function getDimensionsFromProfile(
  profile: CogniPrintProfile,
  isFree: boolean,
): DimensionDisplayData[] {
  const confidenceFor = (key: string): number => {
    const entry = (profile as unknown as Record<string, { confidence?: number }>)[key];
    return typeof entry?.confidence === 'number' ? entry.confidence : 0.5;
  };

  // A dimension with no evidence behind it (never assessed, or stood in for with a
  // centre value because the AI payload omitted it) must not be shown as a finding.
  // Information Density and Viewpoint Range used to coerce a null score to 0, which
  // rendered as "Low" — the product asserted a preference it had never measured.
  const assessedFor = (key: string): boolean =>
    key === 'priorKnowledge' ? true : isAssessed(profile, key as DiagnosticDimensionKey);

  type Dim = (typeof DIMENSION_DISPLAY)[number];
  const base = (dim: Dim, locked: boolean) => ({
    key: dim.key, name: dim.name, color: dim.colour, description: dim.description,
    tier: dim.tier, locked, evidence: evidenceStatusFor(dim.key),
  });

  const notAssessed = (dim: Dim): DimensionDisplayData => ({
    ...base(dim, false),
    value: 0, label: 'Not assessed', summary: NOT_ASSESSED_SUMMARY,
    confidence: 0, assessed: false, kind: 'scalar', showNumber: false,
  });

  const lockedRow = (dim: Dim): DimensionDisplayData => ({
    ...base(dim, true),
    value: 0, label: 'Locked', summary: '', confidence: 0, assessed: false,
    kind: 'scalar', showNumber: false,
  });

  return DIMENSION_DISPLAY.map((dim): DimensionDisplayData => {
    const locked = isFree && dim.tier === 'paid';
    if (locked) return lockedRow(dim);

    const confidence = confidenceFor(dim.key);
    const assessed = assessedFor(dim.key);
    if (!assessed && dim.key !== 'priorKnowledge') return notAssessed(dim);

    const common = { ...base(dim, false), confidence, assessed };

    switch (dim.key) {
      case 'contentFormat': {
        const segments = toSegments({ ...profile.contentFormat.score }, FORMAT_LABELS, dim.colour);
        const clarity = clarityOf(segments.map((x) => x.share));
        const label = weightedLabel(segments, clarity);
        return {
          ...common, kind: 'weighted', segments, clarity, showNumber: false,
          value: segments[0].share, label,
          summary: buildDimensionSummary(dim.key, label),
        };
      }
      case 'learningApproach': {
        const la = profile.learningApproach.score;
        const label = getLearningApproachLabel(la);
        const axes = [
          { key: 'theoryApplication', lowPole: 'Try it first', highPole: 'Understand it first', value: la.theoryApplication, balanced: isLearningAxisBalanced(la.theoryApplication) },
          { key: 'reflectiveActive', lowPole: 'Learn by doing', highPole: 'Watch and think first', value: la.reflectiveActive, balanced: isLearningAxisBalanced(la.reflectiveActive) },
        ];
        // `value` is only used for ring/glow visuals; it is never shown as a number.
        const value = Math.round(Math.max(...axes.map((a) => Math.abs(a.value - 50))) * 2);
        return {
          ...common, kind: 'axes', axes, showNumber: false, value, label,
          summary: buildDimensionSummary(dim.key, label),
        };
      }
      case 'depthTolerance': {
        const value = profile.depthTolerance.score;
        const label = getDepthCategory(value);
        return { ...common, kind: 'scalar', showNumber: true, value, label, summary: buildDimensionSummary(dim.key, label) };
      }
      case 'sequencing': {
        const segments = toSegments({ ...profile.sequencing.score }, SEQUENCING_LABELS, dim.colour);
        const clarity = clarityOf(segments.map((x) => x.share));
        const label = weightedLabel(segments, clarity);
        return {
          ...common, kind: 'weighted', segments, clarity, showNumber: false,
          value: segments[0].share, label,
          summary: buildDimensionSummary(dim.key, label),
        };
      }
      case 'infoDensity': {
        const value = profile.infoDensity!.score;
        const label = getInfoDensityCategory(value);
        return { ...common, kind: 'scalar', showNumber: true, value, label, summary: buildDimensionSummary(dim.key, label) };
      }
      case 'motivation': {
        const segments = toSegments({ ...profile.motivation!.score }, MOTIVATION_LABELS, dim.colour);
        const clarity = clarityOf(segments.map((x) => x.share));
        const label = weightedLabel(segments, clarity);
        return {
          ...common, kind: 'weighted', segments, clarity, showNumber: false,
          value: segments[0].share, label,
          summary: buildDimensionSummary(dim.key, label),
        };
      }
      case 'viewpointRange': {
        const value = profile.viewpointRange!.score;
        const label = getViewpointCategory(value);
        return { ...common, kind: 'scalar', showNumber: true, value, label, summary: buildDimensionSummary(dim.key, label) };
      }
      case 'priorKnowledge':
        // Per-upload: there is no diagnostic score, so no bar, no number, no confidence.
        return {
          ...common, kind: 'perUpload', showNumber: false, value: 0, confidence: 0,
          label: 'Per-upload', summary: buildDimensionSummary(dim.key, 'Per-upload'),
        };
      default:
        return { ...common, kind: 'scalar', showNumber: false, value: 0, label: 'Unknown', summary: '' };
    }
  });
}
